/**
 * Google Payの支払いトークンを取得する
 *
 * 支払いがユーザ操作で中止された場合はABORT_BY_USERが返される
 * @param totalPrice 支払い金額
 * @returns トークン文字列
 */
export const getGooglePaymentToken = async (
  totalPrice: string
): Promise<string | undefined> => {
  const tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification =
    {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: 'gmopg',
        gatewayMerchantId: process.env.NEXT_PUBLIC_GMO_API_KEY ?? '',
      },
    };

  const allowedCardNetworks: google.payments.api.CardNetwork[] = [
    'AMEX',
    'DISCOVER',
    'JCB',
    'MASTERCARD',
    'VISA',
  ];

  const allowedCardAuthMethods: google.payments.api.CardAuthMethod[] = [
    'PAN_ONLY',
    'CRYPTOGRAM_3DS',
  ];

  const baseCardPaymentMethod: google.payments.api.IsReadyToPayPaymentMethodSpecification =
    {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: allowedCardAuthMethods,
        allowedCardNetworks: allowedCardNetworks,
      },
    };

  const baseRequest: google.payments.api.IsReadyToPayRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [baseCardPaymentMethod],
  };

  const cardPaymentMethod = Object.assign(
    { tokenizationSpecification: tokenizationSpecification },
    baseCardPaymentMethod
  );

  const env: google.payments.api.Environment = (process.env
    .NEXT_PUBLIC_GOOGLE_PAY_MODE ?? 'TEST') as google.payments.api.Environment;

  const paymentsClient = new google.payments.api.PaymentsClient({
    environment: env,
  });

  const isReadyToPayRequest: google.payments.api.IsReadyToPayRequest = {
    ...baseRequest,
    allowedPaymentMethods: [baseCardPaymentMethod],
  };

  const paymentToken = await paymentsClient
    .isReadyToPay(isReadyToPayRequest)
    .then(async (response) => {
      if (response.result) {
        const merchant_id = process.env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_ID;
        if (merchant_id == undefined) {
          return;
        }
        const paymentDataRequest: google.payments.api.PaymentDataRequest = {
          ...baseRequest,
          allowedPaymentMethods: [cardPaymentMethod],
          transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: totalPrice,
            currencyCode: 'JPY',
            countryCode: 'JP',
          },
          merchantInfo: {
            merchantId: merchant_id,
            merchantName: process.env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_NAME,
          },
        };

        const payToken = await paymentsClient
          .loadPaymentData(paymentDataRequest)
          .then(async (paymentData) => {
            const paymentToken =
              paymentData.paymentMethodData.tokenizationData.token;
            return paymentToken;
          })
          .catch((e: Error) => {
            if (e.name === 'AbortError') {
              return 'ABORT_BY_USER';
            }
            return undefined;
          });

        return payToken;
      }
    });

  return paymentToken;
};
